import { Injectable } from '@angular/core';

export type ActionType = 'CLOCK_IN' | 'CLOCK_OUT' | 'JOB_START' | 'JOB_END';

export interface QueuedAction {
  id: string;
  type: ActionType;
  tenant_id: string;
  resource_id: string;
  booking_id?: string;
  timestamp: string;
  lat: number | null;
  lng: number | null;
  location_unavailable: boolean;
  gps_denied: boolean;
  form_data?: object;
  attachment_keys?: string[];
  created_at: string;
  retry_count: number;
  last_error?: string;
}

const DB_NAME = 'offline-queue';
const DB_VERSION = 1;
const STORE_NAME = 'actions';

@Injectable({ providedIn: 'root' })
export class OfflineQueueService {
  private dbPromise: Promise<IDBDatabase> | null = null;

  /** Generate a unique ID for a queued action */
  generateId(): string {
    return crypto.randomUUID();
  }

  private openDb(): Promise<IDBDatabase> {
    if (this.dbPromise) {
      return this.dbPromise;
    }

    this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('by_type', 'type', { unique: false });
          store.createIndex('by_created_at', 'created_at', { unique: false });
        }
      };

      request.onsuccess = (event) => {
        resolve((event.target as IDBOpenDBRequest).result);
      };

      request.onerror = (event) => {
        this.dbPromise = null;
        reject((event.target as IDBOpenDBRequest).error);
      };
    });

    return this.dbPromise;
  }

  /** Add a new action to the offline queue */
  async addAction(action: QueuedAction): Promise<void> {
    const db = await this.openDb();
    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.add(action);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /** Get a single action by ID, or undefined if not found */
  async getAction(id: string): Promise<QueuedAction | undefined> {
    const db = await this.openDb();
    return new Promise<QueuedAction | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result as QueuedAction | undefined);
      request.onerror = () => reject(request.error);
    });
  }

  /** Get all queued actions ordered by created_at ascending */
  async getAllActions(): Promise<QueuedAction[]> {
    const db = await this.openDb();
    return new Promise<QueuedAction[]>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('by_created_at');
      const request = index.getAll();

      request.onsuccess = () => resolve(request.result as QueuedAction[]);
      request.onerror = () => reject(request.error);
    });
  }

  /** Get all queued actions filtered by action type */
  async getByType(type: ActionType): Promise<QueuedAction[]> {
    const db = await this.openDb();
    return new Promise<QueuedAction[]>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('by_type');
      const request = index.getAll(type);

      request.onsuccess = () => resolve(request.result as QueuedAction[]);
      request.onerror = () => reject(request.error);
    });
  }

  /** Update an action in the queue (e.g. to increment retry_count) */
  async updateAction(action: QueuedAction): Promise<void> {
    const db = await this.openDb();
    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const putRequest = store.put(action);

      putRequest.onsuccess = () => resolve();
      putRequest.onerror = () => reject(putRequest.error);
    });
  }

  /** Delete an action from the queue by ID */
  async deleteAction(id: string): Promise<void> {
    const db = await this.openDb();
    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /** Get the count of queued actions */
  async getCount(): Promise<number> {
    const db = await this.openDb();
    return new Promise<number>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.count();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /** Clear all actions from the queue */
  async clearAll(): Promise<void> {
    const db = await this.openDb();
    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}
